import {
    faChevronDown,
    faChevronUp,
    faMinusCircle,
    faPlusCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import React from "react";

import { WikiArticleEntity } from "@/mk2/apps/wiki/schemas";
import { RenderSnippetAs } from "@/mk2/apps/wiki/utils";
import { PhotosRowOfSquares } from "@/mk2/components/PhotosRowOfSquares";
import { astToHtml, defaultFilterAdditionalData } from "@/mk2/helpers/article_ast/filters";
import { dateLocal } from "@/mk2/helpers/timeformat";
import { PhotoEntity } from "@/mk2/schemas";
import { interpolate } from "@/mk2/services/i18n";
import {
    WIKI_AI_REVIEW_SUMMARY_BADGE,
    WIKI_AI_REVIEW_SUMMARY_DISCLAIMER,
    WIKI_AI_REVIEW_SUMMARY_DRAFT,
    WIKI_AI_REVIEW_SUMMARY_HIDE,
    WIKI_AI_REVIEW_SUMMARY_NEGATIVES,
    WIKI_AI_REVIEW_SUMMARY_POSITIVES,
    WIKI_AI_REVIEW_SUMMARY_SHOW,
    count_WIKI_AI_REVIEW_SUMMARY_SUMMARIZED_FROM,
} from "@/mk/autogenerated/translations/AIReviewSummaryCard.a61129fd1b7b233c712f0e45d4f0942f";

import styles from "./AIReviewSummaryCard.mscss";

// Card body is ~MAX_WIDTH (584) minus the card border and body padding; this caps
// how many thumbnails render before the "+N" tile.
const PHOTOS_ROW_MAX_WIDTH = 560;

interface OwnProps {
    article: WikiArticleEntity;
    photos?: PhotoEntity[];
    galleryLinkTo?: (photo: PhotoEntity) => string | null;
}

interface State {
    isExpanded: boolean;
}

/**
 * AI-generated summary of all reviews of a product, shown at the very top of a
 * review page (above the gallery): average star rating, an "average
 * description" paragraph and the most-common pros/cons. Collapsible like the
 * forum AIThreadSummaryCard. Regular users see it only once published; wiki
 * editors also see unpublished drafts, marked with a "draft" badge.
 */
export default class AIReviewSummaryCard extends React.Component<OwnProps, State> {
    public state: State = { isExpanded: false };

    private toggle = () => {
        this.setState((s) => ({ isExpanded: !s.isExpanded }));
    };

    private handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            this.toggle();
        }
    };

    public render() {
        const { article, photos, galleryLinkTo } = this.props;
        if (!article.aiReviewSummaryAST) {
            return null;
        }

        const { isExpanded } = this.state;
        const positives = article.aiReviewSummaryPositives ?? [];
        const negatives = article.aiReviewSummaryNegatives ?? [];
        // Backend only sends an unpublished summary to wiki editors (admin
        // preview) — regular users never receive it. So AST present + no
        // updatedAt ⇒ this is an admin-only draft.
        const isDraft = !article.aiReviewSummaryUpdatedAt;

        const additionalData = {
            ...defaultFilterAdditionalData,
            renderSnippetObjectsAs: RenderSnippetAs.HTML_IFRAME,
            renderSpecialWikiLinks: false,
            linkedObjects: article.aiReviewSummaryASTAdditionalData?.linkedObjects || {},
            photos: [],
            rootDiv: false,
            hashizeFn: (chunk) => chunk,
        };

        return (
            <div
                className={cx(
                    styles.AIReviewSummaryCard,
                    isExpanded && styles["AIReviewSummaryCard--expanded"],
                )}
                role="region"
                aria-label={WIKI_AI_REVIEW_SUMMARY_BADGE}
                data-ai-generated="true"
                data-ai-generated-at={article.aiReviewSummaryUpdatedAt || undefined}
            >
                <div className={styles.AIReviewSummaryCard__header}>
                    <span className={styles.AIReviewSummaryCard__badge}>
                        {WIKI_AI_REVIEW_SUMMARY_BADGE}
                    </span>
                    {isDraft && (
                        <span className={styles.AIReviewSummaryCard__draftBadge}>
                            {WIKI_AI_REVIEW_SUMMARY_DRAFT}
                        </span>
                    )}
                </div>

                <div className={styles.AIReviewSummaryCard__body}>
                    <div
                        className={cx(
                            !isExpanded && styles["AIReviewSummaryCard__summary--collapsed"],
                        )}
                    >
                        {astToHtml(article.aiReviewSummaryAST, additionalData)}
                    </div>

                    <div
                        className={styles.AIReviewSummaryCard__summaryToggle}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                        onClick={this.toggle}
                        onKeyDown={this.handleKeyDown}
                    >
                        <span className={styles.AIReviewSummaryCard__hint}>
                            {isExpanded ? WIKI_AI_REVIEW_SUMMARY_HIDE : WIKI_AI_REVIEW_SUMMARY_SHOW}
                        </span>
                        <FontAwesomeIcon
                            icon={isExpanded ? faChevronUp : faChevronDown}
                            className={styles.AIReviewSummaryCard__chevron}
                        />
                    </div>

                    {positives.length > 0 && (
                        <ul className={styles.AIReviewSummaryCard__positivesList}>
                            <li className={styles.AIReviewSummaryCard__listHeader}>
                                {WIKI_AI_REVIEW_SUMMARY_POSITIVES}
                            </li>
                            {positives.map((point, index) => (
                                <li className={styles.AIReviewSummaryCard__listItem} key={index}>
                                    <div
                                        className={
                                            styles.AIReviewSummaryCard__listItem__iconContainer
                                        }
                                    >
                                        <FontAwesomeIcon icon={faPlusCircle} />
                                    </div>
                                    {point}
                                </li>
                            ))}
                        </ul>
                    )}

                    {negatives.length > 0 && (
                        <ul className={styles.AIReviewSummaryCard__negativesList}>
                            <li className={styles.AIReviewSummaryCard__listHeader}>
                                {WIKI_AI_REVIEW_SUMMARY_NEGATIVES}
                            </li>
                            {negatives.map((point, index) => (
                                <li className={styles.AIReviewSummaryCard__listItem} key={index}>
                                    <div
                                        className={
                                            styles.AIReviewSummaryCard__listItem__iconContainer
                                        }
                                    >
                                        <FontAwesomeIcon icon={faMinusCircle} />
                                    </div>
                                    {point}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {photos && photos.length > 0 && (
                    <PhotosRowOfSquares
                        className={cx(
                            styles.AIReviewSummaryCard__photosRow,
                            photos.length > 6
                                ? styles["AIReviewSummaryCard__photosRow--stretchedAndCentered"]
                                : styles["AIReviewSummaryCard__photosRow--alignLeft"],
                        )}
                        photoVersion="s246x246"
                        maxWidth={PHOTOS_ROW_MAX_WIDTH}
                        photos={photos}
                        photoAlt={article.title}
                        linkTo={galleryLinkTo}
                    />
                )}

                <div className={styles.AIReviewSummaryCard__intro}>
                    {article.aiReviewSummaryInputCount
                        ? interpolate(count_WIKI_AI_REVIEW_SUMMARY_SUMMARIZED_FROM, {
                              count: article.aiReviewSummaryInputCount,
                          })
                        : WIKI_AI_REVIEW_SUMMARY_DISCLAIMER}
                    {article.aiReviewSummaryGeneratedAt && (
                        <> &middot; {dateLocal(article.aiReviewSummaryGeneratedAt)}</>
                    )}
                </div>
            </div>
        );
    }
}
